const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:18009/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

export const getPublicApiBaseUrl = () => {
  const adminBase = getAdminBaseUrl();
  return adminBase.replace(/\/admin\/?$/, "");
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
  if (!token) return { "Content-Type": "application/json" };
  return {
    "Content-Type": "application/json",
    Authorization: token.startsWith("Basic ") || token.startsWith("Bearer ") ? token : `Bearer ${token}`,
  };
};

const isApiFailure = (result) => {
  if (!result || typeof result !== "object") return false;
  if (result.status === false) return true;
  if (result.success === false) return true;
  return false;
};

const readResponseBody = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  return isJson ? await response.json() : await response.text();
};

export const isMissingAdsSettingMessage = (message) =>
  /ads setting not found/i.test(message || "") || /^not found$/i.test((message || "").trim());

/** Normalize GET /ad-keys (and similar) — `data` may be an array or an object with keys/data. */
export const extractPlacementKeysPayload = (result) => {
  const emptyGoogleAds = { android: {}, ios: {} };
  if (!result || typeof result !== "object") {
    return { keys: [], categories: [], googleAds: emptyGoogleAds };
  }

  const topKeys = Array.isArray(result.keys) ? result.keys : null;
  const block = result.data !== undefined ? result.data : result;

  if (Array.isArray(block)) {
    return {
      keys: block,
      categories: result.categories ?? [],
      googleAds: result.googleAds ?? emptyGoogleAds,
      isAdsRun: result.isAdsRun,
      adsType: result.adsType,
      adsSettingId: result.adsSettingId,
    };
  }

  if (block && typeof block === "object") {
    let keys =
      (Array.isArray(block.keys) && block.keys) ||
      (Array.isArray(block.data) && block.data) ||
      topKeys ||
      [];
    const rowName = (row) => row?.keyName || row?.key || row?.name || "";
    if (
      keys.length === 0 &&
      block.id != null &&
      rowName(block)
    ) {
      keys = [block];
    }
    if (keys.length === 0 && result.key && typeof result.key === "object") {
      keys = [result.key];
    }
    return {
      keys,
      categories: block.categories ?? result.categories ?? [],
      googleAds: block.googleAds ?? result.googleAds ?? emptyGoogleAds,
      isAdsRun: block.isAdsRun ?? result.isAdsRun,
      adsType: block.adsType ?? result.adsType,
      adsSettingId: block.adsSettingId ?? result.adsSettingId,
    };
  }

  return {
    keys: topKeys || [],
    categories: result.categories ?? [],
    googleAds: result.googleAds ?? emptyGoogleAds,
  };
};

const handleApiResponse = async (response) => {
  const result = await readResponseBody(response);

  if (!response.ok || isApiFailure(result)) {
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message || result?.error || result?.message)) ||
      (typeof result === "string" ? result : "API request failed");
    const err = new Error(errorMsg);
    err.status = response.status;
    throw err;
  }

  return result;
};

export const getAdKeys = async () => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-keys`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const createAdKey = async (keyName) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-keys`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ keyName }),
  });
  return handleApiResponse(response);
};

export const updateAdKey = async (id, keyName) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-keys/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify({ keyName }),
  });
  return handleApiResponse(response);
};

export const deleteAdKey = async (id) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-keys/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const saveAdKeyUnitIds = async (payload) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-keys`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse(response);
};

export const getAdsSetting = async () => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ads-setting`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  const result = await readResponseBody(response);

  if (!response.ok) {
    const errorMsg =
      (typeof result === "object" && (result?.message || result?.error)) ||
      "Failed to load ads setting";
    const err = new Error(errorMsg);
    err.status = response.status;
    throw err;
  }

  if (typeof result === "object" && result.success === false) {
    if (isMissingAdsSettingMessage(result.message)) {
      return { success: true, data: null };
    }
    throw new Error(result.message || "Failed to load ads setting");
  }

  if (typeof result === "object" && result.status === false) {
    if (isMissingAdsSettingMessage(result.message)) {
      return { success: true, data: null };
    }
    throw new Error(result.message || "Failed to load ads setting");
  }

  return result;
};

export const createAdsSetting = async (body) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ads-setting`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleApiResponse(response);
};

export const updateAdsSetting = async (id, body) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ads-setting/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleApiResponse(response);
};

export const getPublicAdSetting = async () => {
  const response = await fetch(`${getPublicApiBaseUrl()}/api/v1/adsetting`, {
    method: "GET",
  });
  return handleApiResponse(response);
};

export const getAdConfig = async () => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-config`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const getAdNetworks = async () => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const createAdNetwork = async (body) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  return handleApiResponse(response);
};

export const activateAdNetwork = async (id) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${id}/activate`, {
    method: "PUT",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const deleteAdNetwork = async (id) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const getAdNetwork = async (id) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${id}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

export const updateAdNetwork = async (id, payload) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleApiResponse(response);
};

export const addAdNetworkKey = async (networkId, keyName) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${networkId}/keys`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ keyName }),
  });
  return handleApiResponse(response);
};

export const parseImportJsonText = (text) => {
  const trimmed = String(text || "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    throw new Error("Paste JSON first");
  }
  return JSON.parse(trimmed);
};

/** Accept paste from import-json body, PUT body, or GET /api/v1/adsetting envelope. */
export const normalizeGoogleAdsFromImport = (parsed) => {
  if (!parsed || typeof parsed !== "object") return null;

  const pickPlatforms = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    if (obj.googleAds && (obj.googleAds.android || obj.googleAds.ios)) {
      return {
        android: obj.googleAds.android ?? {},
        ios: obj.googleAds.ios ?? {},
      };
    }
    if (obj.android || obj.ios) {
      return {
        android: obj.android ?? {},
        ios: obj.ios ?? {},
      };
    }
    return null;
  };

  return (
    pickPlatforms(parsed) ||
    pickPlatforms(parsed.data) ||
    pickPlatforms(parsed.data?.data) ||
    null
  );
};

export const importAdNetworkJson = async (networkId, googleAds) => {
  const response = await fetch(`${getAdminBaseUrl()}/setting/ad-networks/${networkId}/import-json`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ googleAds }),
  });
  return handleApiResponse(response);
};

/** POST import-json; if route missing, fall back to PUT network with same googleAds payload. */
export const importAdNetworkGoogleAds = async (networkId, googleAds) => {
  try {
    return await importAdNetworkJson(networkId, googleAds);
  } catch (err) {
    const usePutFallback =
      err.status === 404 ||
      err.status === 405 ||
      /import-json/i.test(err.message || "") ||
      /cannot post/i.test(err.message || "") ||
      /not found/i.test(err.message || "");
    if (!usePutFallback) throw err;
    return updateAdNetwork(networkId, { googleAds });
  }
};

export const collectKeyNamesFromGoogleAds = (googleAds) => {
  if (!googleAds) return [];
  return [
    ...new Set([
      ...Object.keys(googleAds.android || {}),
      ...Object.keys(googleAds.ios || {}),
    ]),
  ].sort();
};

/** Server / README format: lowercase + underscores (nativeGame → native_game). */
export const canonicalPlacementKeyName = (name) => {
  if (!name) return "";
  return String(name)
    .trim()
    .replace(/-/g, "_")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();
};

export const isValidPlacementKeyName = (name) => /^[a-z][a-z0-9_]*$/.test(name);

export const remapGoogleAdsKeyNames = (googleAds, rename = canonicalPlacementKeyName) => {
  const mapSide = (side) => {
    const out = {};
    Object.entries(side || {}).forEach(([key, value]) => {
      const next = rename(key);
      if (!next) return;
      const existing = out[next];
      if (existing === undefined || existing === "" || existing == null) {
        out[next] = value;
      }
    });
    return out;
  };
  return {
    android: mapSide(googleAds?.android),
    ios: mapSide(googleAds?.ios),
  };
};

export const getGoogleAdsKeyRenameNotes = (googleAds) => {
  const notes = [];
  collectKeyNamesFromGoogleAds(googleAds).forEach((orig) => {
    const canon = canonicalPlacementKeyName(orig);
    if (canon && canon !== orig) notes.push(`${orig} → ${canon}`);
  });
  return notes;
};

export const filterGoogleAdsToKeys = (googleAds, allowedNames) => {
  const allowed = new Set((allowedNames || []).filter(Boolean));
  const filterSide = (side) => {
    const out = {};
    Object.entries(side || {}).forEach(([key, value]) => {
      if (allowed.has(key)) out[key] = value;
    });
    return out;
  };
  return {
    android: filterSide(googleAds?.android),
    ios: filterSide(googleAds?.ios),
  };
};

/** Create global placement keys missing from JSON (required before units show in admin). */
export const ensurePlacementKeysForGoogleAds = async (networkId, googleAds, existingKeyNames = []) => {
  const existing = new Set(existingKeyNames.filter(Boolean));
  const names = collectKeyNamesFromGoogleAds(googleAds);
  const created = [];
  const skipped = [];
  const failed = [];

  for (const keyName of names) {
    const canonical = canonicalPlacementKeyName(keyName);
    if (!isValidPlacementKeyName(canonical)) {
      failed.push({ keyName, message: "Invalid key name format" });
      continue;
    }
    if (existing.has(canonical)) {
      skipped.push(canonical);
      continue;
    }
    try {
      if (networkId != null) {
        await addAdNetworkKey(networkId, canonical);
      } else {
        await createAdKey(canonical);
      }
      existing.add(canonical);
      created.push(canonical);
    } catch (err) {
      const msg = err.message || "Failed";
      if (/already exists|duplicate/i.test(msg)) {
        existing.add(canonical);
        skipped.push(canonical);
      } else {
        failed.push({ keyName, message: msg });
      }
    }
  }

  return { created, skipped, failed, allNames: [...existing] };
};
